import { Navigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { ContentLoader } from '@/components/common';
import { useAuthStore } from '@/store';
// C1/P1: the profile, KYC, agent and password blocks that used to live here
// now live under Settings; the pieces they share are exported from there.
import { useCustomerMe } from '@/pages/settings/portalProfile';
import { AdvisorCards } from '@/pages/settings/AdvisorCards';

/**
 * The POSP's "My Broker" page.
 *
 * This route used to be the customer's "My Contacts" too. M-01/U-01 folded the
 * customer's copy into Settings > Profile ("Your advisors", same cards, same
 * `/customers/me/` read), so a customer arriving here -- an old bookmark, an
 * old email -- is sent there instead. Nothing was stored behind the page, so
 * nothing is lost by the move.
 *
 * For a POSP it is unchanged: their broker, and not their own card (their own
 * details are on /settings > Profile -- they are not a contact of themselves).
 */
export function MyContactsPage() {
  const role = useAuthStore((state) => state.user?.role);
  if (role === 'customer') {
    return <Navigate to={{ pathname: '/settings', hash: '#advisors' }} replace />;
  }
  return <MyContactsContent />;
}

function MyContactsContent() {
  const { data, isLoading, error, refetch } = useCustomerMe();

  if (isLoading) return <ContentLoader />;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Failed to load contact information.</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Try again
        </button>
      </div>
    );
  }

  // A POSP login has no Customer row, so `/customers/me/` returns their broker
  // plus their OWN agent details. Their own card is not a contact, so the
  // agent card is only drawn for a customer payload.
  const isCustomer = !!data?.customer_code;

  return (
    <div>
      <PageHeader
        title="My Contacts"
        subtitle="Your assigned broker and advisor contact details"
      />

      <div className="mt-6">
        <AdvisorCards
          broker={{
            name: data?.broker_name,
            contactPerson: data?.contact_person_name,
            phone: data?.broker_phone,
            email: data?.broker_email,
          }}
          agent={{ name: data?.posp_name, phone: data?.posp_phone, email: data?.posp_email }}
          showAgent={isCustomer}
        />
      </div>
    </div>
  );
}
